/**
 * Máquina de estados da clarificação logística (pura, sem IO).
 *
 * Uma única fonte de verdade para o painel inline: cada escolha devolve um
 * novo estado visível ou uma resposta em texto para o pedido original.
 * Nada aqui chama Places, Routes, Photos, IA ou créditos.
 */

import { minutesToHHMM, type TravelLogistics } from "./arrival-window";

export type LogisticsStage =
  | "arrival_time"
  | "arrival_kind"
  | "arrival_airport"
  | "departure_time"
  | "departure_kind"
  | "departure_airport"
  | "hotel_selection"
  | "none";

export type LogisticsMissing = "logistics_arrival" | "logistics_departure";

export type LogisticsTransition =
  | { type: "stage"; stage: LogisticsStage; openHotelPicker?: boolean }
  | { type: "answer"; answer: string };

/** Estado inicial: nunca perguntar uma hora que já é conhecida. */
export function baseLogisticsStage(missing: LogisticsMissing, logistics: TravelLogistics): LogisticsStage {
  if (missing === "logistics_arrival") {
    if (logistics.arrival?.timeMinutes == null) return "arrival_time";
    // Aterragem declarada sem aeroporto: perguntar o aeroporto, não o tipo.
    const airportName = logistics.arrival.airportName?.trim() ?? "";
    if (logistics.arrival.kind === "airport" && airportName.length < 3) return "arrival_airport";
    return "arrival_kind";
  }
  if (logistics.departure?.timeMinutes == null) return "departure_time";
  // "vou embora às 17h": tipo de saída por esclarecer antes do aeroporto.
  if (logistics.departure.kindUnknown) return "departure_kind";
  return "departure_airport";
}


/** Hora conhecida (HH:MM) da chegada / partida, ou null. */
export function knownTimeHHMM(missing: LogisticsMissing, logistics: TravelLogistics): string | null {
  const minutes =
    missing === "logistics_arrival" ? logistics.arrival?.timeMinutes : logistics.departure?.timeMinutes;
  return minutes == null ? null : minutesToHHMM(minutes);
}

export type ArrivalChoice = "airport" | "city" | "hotel";

/** Escolha de tipo de chegada nos chips. */
export function chooseArrivalKind(
  choice: ArrivalChoice,
  ctx: { arrivalHHMM?: string | null; hotelSelected?: boolean; knownAirport?: string | null },
): LogisticsTransition {
  const at = ctx.arrivalHHMM ? ` às ${ctx.arrivalHHMM}` : "";
  const knownAirport = (ctx.knownAirport ?? "").trim();
  if (choice === "airport") {
    // O aeroporto já vem do pedido original (ex.: "JFK"): nunca voltar a perguntá-lo.
    if (knownAirport.length >= 3) {
      if (ctx.hotelSelected) return { type: "answer", answer: `aterro no aeroporto de ${knownAirport}${at}` };
      return { type: "stage", stage: "hotel_selection", openHotelPicker: true };
    }
    return { type: "stage", stage: "arrival_airport" };
  }
  if (choice === "city") return { type: "answer", answer: `estou na cidade${at}` };
  if (ctx.hotelSelected) return { type: "answer", answer: `chego ao hotel${at}` };
  // Sem hotel escolhido nunca se inventa hotel, morada, check-in ou bagagem.
  return { type: "stage", stage: "hotel_selection", openHotelPicker: true };
}

/** Normaliza um nome escrito à mão ("el prat" → "El Prat"). */
export function normalizePlaceName(raw: string): string {
  return (raw ?? "")
    .trim()
    .replace(/\s+/gu, " ")
    .replace(/(^|\s)(\p{L})/gu, (_m, sep: string, c: string) => `${sep}${c.toLocaleUpperCase()}`);
}

/** Resposta de aeroporto (chegada ou partida). */
export function airportAnswer(raw: string, ctx: { isDeparture: boolean; hhmm?: string | null }): string {
  const name = normalizePlaceName(raw);
  const at = ctx.hhmm ? ` às ${ctx.hhmm}` : "";
  return ctx.isDeparture ? `partida do aeroporto de ${name}${at}` : `aterro no aeroporto de ${name}${at}`;
}

/** Resposta de hotel escrito à mão — não cria check-in por si só. */
export function hotelAnswer(raw: string, hhmm?: string | null, arrivalAirport?: string | null): string {
  const name = normalizePlaceName(raw);
  const at = hhmm ? ` às ${hhmm}` : "";
  const airport = (arrivalAirport ?? "").trim();
  // Chegada por aeroporto já conhecida: a resposta do hotel nunca a apaga.
  if (airport.length >= 3) return `aterro no aeroporto de ${airport}${at} e fico no hotel ${name}`;
  return `chego ao hotel ${name}${at}`;
}

export type DepartureChoice = "flight" | "city";

/**
 * Escolha do tipo de saída. "Só saio da cidade" devolve um marcador
 * determinístico ("saída por terra") — nunca cria aeroporto nem voo.
 */
export function chooseDepartureKind(
  choice: DepartureChoice,
  ctx: { departureHHMM?: string | null },
): LogisticsTransition {
  if (choice === "flight") return { type: "stage", stage: "departure_airport" };
  const at = ctx.departureHHMM ? ` às ${ctx.departureHHMM}` : "";
  return { type: "answer", answer: `parto da cidade${at} (saída por terra)` };
}
